<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'userManagement',
})
</script>
<template>
  <router-view v-if="$route.name !== 'quotationRecord'" />
  <div v-else class="main">
    <a-card>
      <div class="system-dictionary-search">
        <a-form :model="state.tableData.param" class="global-search-form">
          <a-row :gutter="24">
            <a-col :xs="24" :sm="24" :md="12" :lg="5" :xl="5" class="mb-[10px]">
              <a-form-item label="报价单号:">
                <a-input v-model:value="state.tableData.param.quotationNo" placeholder="请输入报价单号" :maxlength="20"
                  allow-clear />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="5" :xl="5" class="mb-[10px]">
              <a-form-item label="省/市:">
                <a-cascader v-model:value="state.tableData.param.areaId" :options="state.areaIdList"
                  placeholder="请选择省/市" :field-names="{ label: 'label', value: 'id', children: 'children' }" />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="4" :xl="4" class="mb-[10px]">
              <a-form-item label="店铺名称:">
                <a-input v-model:value="state.tableData.param.storeName" placeholder="请输入店铺名称" :maxlength="30"
                  allow-clear />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="4" :xl="4" class="mb-[10px]">
              <a-form-item label="手机号:">
                <a-input v-model:value="state.tableData.param.phone" placeholder="请输入手机号" :maxlength="11" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :xs="24" :sm="24" :md="12" :lg="6" :xl="6" class="mb-[10px]">
              <div class="card-col-action flex flex-nowrap">
                <ant-button class="search mr-[10px]!" @click="getTableData" type="primary">
                  <SearchOutlined />查询
                </ant-button>
                <ant-button class="resetFun" @click="resetFun" type="primary">
                  <RedoOutlined />重置
                </ant-button>
              </div>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <ant-table :columns="columns" :data-source="state.tableData.data" :loading="state.tableData.loading"
        :pagination="false" row-key="id">
        <template #bodyCell="{ column, record, index }">
          <template v-if="column.key === 'index'">{{ index + 1 }}</template>
          <template v-if="column.key === 'action'">
            <div>
              <ant-button type="link" size="small" @click="openDialog(record)">查看详情</ant-button>
              <a-popconfirm :title="`确定删除该条报价记录?`" @confirm="onDelete(record)">
                <ant-button type="link" size="small" danger v-auth="'quotation.record.del'">删除</ant-button>
              </a-popconfirm>
            </div>
          </template>
        </template>
      </ant-table>
      <ant-pagination v-model:current="state.tableData.param.pageNum" v-model:page-size="state.tableData.param.pageSize"
        :total="state.tableData.total" :page-size-options="['10', '20']" show-size-changer
        :show-total="(total) => `共 ${total} 条`" :showQuickJumper="true" @change="onPageChange"
        @show-size-change="onShowSizeChange" />
    </a-card>
  </div>
</template>

<script setup lang="ts" name="userManagement">
import { reactive, onMounted } from 'vue'
import { useQuotation } from '@/store/quotation/quotation'
import { useDict } from '@/store/dict'
import { fetchWithPaginationFallback } from '@/utils/paginationFallback'
import { message } from 'ant-design-vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const quotationApi = useQuotation()
const dictApi = useDict()

const columns = [
  { title: '序号', dataIndex: 'index', key: 'index', width: 60, ellipsis: true },
  { title: '报价单号', dataIndex: 'quotationNo', key: 'quotationNo', width: 200, ellipsis: true },
  { title: '店铺名称', dataIndex: 'storeName', key: 'storeName', width: 200, ellipsis: true },
  { title: '店铺面积', dataIndex: 'buildingArea', key: 'buildingArea', width: 200, ellipsis: true, customRender: ({ text }: { text: any }) => text ? `${text}㎡` : '-' },
  { title: '改造总价', dataIndex: 'totalPrice', key: 'totalPrice', ellipsis: true, customRender: ({ text }: { text: any }) => text !== undefined && text !== null ? Number(text).toFixed(2) : '-' },
  { title: '手机号', dataIndex: 'phone', key: 'phone', ellipsis: true },
  { title: '状态', dataIndex: 'statusStr', key: 'statusStr', ellipsis: true, customRender: ({ text }: { text: string }) => {
    return text && (text === '未失效' || text.includes('未失效')) ? '有效' : text
  } },
  { title: '报价时间', dataIndex: 'quoteTime', key: 'quoteTime', ellipsis: true },
  { title: '操作', key: 'action', width: 150, align: 'center' }
]

const state = reactive({
  areaIdList: [],
  tableData: {
    data: [] as any[],
    total: 0,
    loading: false,
    param: {
      pageNum: 1,
      pageSize: 10,
      quotationNo: '', // 报价单号
      storeName: '', // 店铺名称
      areaId: [], // 省/市
      phone: '' //手机号
    }
  }
})

const getTableData = async () => {
  state.tableData.loading = true
  try {
    const { areaId } = state.tableData.param
    let params = {
      ...state.tableData.param,
      areaId: areaId && areaId.length > 0 ? areaId[areaId.length - 1] : ''
    }
    const data: any = await fetchWithPaginationFallback(
      (param) => quotationApi.getQuotationRecordPage(param),
      params
    )
    state.tableData.data = data.records || []
    state.tableData.total = data.total || 0
    state.tableData.param.pageNum = data.pageNum ?? state.tableData.param.pageNum
  } finally {
    state.tableData.loading = false
  }
}

// 打开弹窗
const openDialog = (record: any) => {
  router.push({ path: '/quotation/record/detail', query: { id: record.id } })
}

// 重置
const resetFun = () => {
  for (let key in state.tableData.param) {
    if (key != 'pageNum' && key != 'pageSize') {
      state.tableData.param[key] = ''
    }
  }
}

// 删除
const onDelete = async (record) => {
  await quotationApi.quotationRecordDel(record.id)
  message.success('删除成功')
  getTableData()
}

const onPageChange = (page: number) => {
  state.tableData.param.pageNum = page
  getTableData()
}

const onShowSizeChange = (_current: number, size: number) => {
  state.tableData.param.pageSize = size
  state.tableData.param.pageNum = 1
  getTableData()
}

onMounted(async () => {
  state.areaIdList = (await dictApi.getAreaTreeCity()) as any
  getTableData()
})
</script>
<style lang="less" scoped></style>
